import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, CircularProgress, Alert, Stack,
  Autocomplete, FormControl, InputLabel, Select, MenuItem,
  useMediaQuery, useTheme
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { financeReportsService } from '../../services/accounting/financeReports.service';
import { exportService } from '../../services/accounting/export.service';
import { serverMessage } from '../../utils/apiConvert';
import { toYmd } from '../../utils/accounting/periodUtils';

const fontSize = {
  xs: '0.75rem',
  sm: '0.8125rem',
  md: '0.875rem',
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED', minimumFractionDigits: 2 }).format(amount || 0);

const formatDate = (date) =>
  date ? new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

const WalletBankReport = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [statement, setStatement] = useState(null);
  const [banks, setBanks] = useState([]);

  // Filters (default: the current month; the account is required)
  const [dateFrom, setDateFrom] = useState(toYmd(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [dateTo, setDateTo] = useState(toYmd(new Date()));
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchParams] = useSearchParams();
  const [selectedBank, setSelectedBank] = useState(searchParams.get('account') || '');
  const [customers, setCustomers] = useState([]);
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
    financeReportsService.getCustomers(user.typingCenterId || user.typing_center_id).then(setCustomers).catch(() => {});
  }, []);
  useEffect(() => {
    financeReportsService.getMoneyAccounts().then((list) => {
      setBanks(list);
      setSelectedBank((cur) => cur || (list.find((b) => b.active) || list[0])?.id || '');
    }).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    if (!selectedBank) { setData([]); setStatement(null); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await financeReportsService.getBankStatement({ bankAccountId: selectedBank, from: dateFrom, to: dateTo, customerId: selectedCustomer?.id });
      // The opening balance leads the lines; money out shows as debit, money in as credit (the bank's own view)
      const opening = { id: 'opening', isOpening: true, date: res?.from, description: 'Opening Balance', client: '-', debit: 0, credit: 0, balance: Number(res?.openingBalance || 0) };
      const lines = (res?.lines || []).map((l, i) => ({
        id: `${l.reference}-${i}`, date: l.date, description: l.description, reference: l.reference, client: l.customer || '-',
        debit: Number(l.moneyOut || 0), credit: Number(l.moneyIn || 0), balance: Number(l.balance || 0),
      }));
      setData([opening, ...lines]);
      setStatement(res);
    } catch (err) {
      setData([]);
      setStatement(null);
      setError(serverMessage(err, 'Failed to load the bank report'));
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, selectedCustomer, selectedBank]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Export with the page's own filters (BE-14b); the file comes back under the server's name
  const [exporting, setExporting] = useState(null);
  const doExport = async (format) => {
    setExporting(format);
    try {
      await exportService.download('bank-statement', format, { bankAccountId: selectedBank, from: dateFrom, to: dateTo, customerId: selectedCustomer?.id });
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(null);
    }
  };

  const paginatedData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const totalDebit = Number(statement?.moneyOutTotal || 0);
  const totalCredit = Number(statement?.moneyInTotal || 0);
  const closingBalance = Number(statement?.closingBalance || 0);

  return (
    <Box sx={{ pb: isMobile ? 2 : 0 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Box>
          <Typography sx={{ fontSize: isMobile ? '1rem' : '1.125rem', fontWeight: 600, color: 'secondary.main' }}>
            Bank Statement
          </Typography>
          <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
            Per-account transaction ledger with running balance (bank, cash and e-wallet)
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button size="small" variant="contained" startIcon={<ExcelIcon sx={{ fontSize: 16 }} />}
            onClick={() => doExport('csv')} disabled={Boolean(exporting)}
            sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, fontSize: fontSize.xs, textTransform: 'none' }}>
            {exporting === 'csv' ? 'Exporting…' : 'CSV'}
          </Button>
          <Button size="small" variant="contained" startIcon={<PdfIcon sx={{ fontSize: 16 }} />}
            onClick={() => doExport('pdf')} disabled={Boolean(exporting)}
            sx={{ bgcolor: '#c62828', '&:hover': { bgcolor: '#b71c1c' }, fontSize: fontSize.xs, textTransform: 'none' }}>
            {exporting === 'pdf' ? 'Exporting…' : 'PDF'}
          </Button>
          <Button size="small" variant="outlined" onClick={fetchData}
            sx={{ minWidth: 36 }}>
            <RefreshIcon sx={{ fontSize: 18 }} />
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Bank Account</InputLabel>
              <Select value={selectedBank} label="Bank Account"
                onChange={(e) => { setSelectedBank(e.target.value); setPage(0); }}>
                {banks.map(b => (
                  <MenuItem key={b.id} value={b.id}>{b.label}{b.active ? '' : ' (inactive)'}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth size="small" label="From Date" type="date"
              value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth size="small" label="To Date" type="date"
              value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={5}>
            <Autocomplete size="small" options={customers}
              value={selectedCustomer}
              onChange={(e, v) => { setSelectedCustomer(v); setPage(0); }}
              renderInput={(params) => <TextField {...params} label="Client / Organization" />}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Table / Cards */}
      {isMobile ? (
        <Stack spacing={1.5}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : paginatedData.length === 0 ? (
            <Paper sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontSize: fontSize.sm }}>No transactions found</Typography></Paper>
          ) : paginatedData.map((row) => (
            <Card key={row.id} variant="outlined" sx={row.isOpening ? { bgcolor: isDark ? 'rgba(66,165,245,0.08)' : '#e3f2fd' } : {}}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={0.5}>
                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: row.isOpening ? 700 : 500 }}>{row.description}</Typography>
                  <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>{formatDate(row.date)}</Typography>
                </Box>
                {!row.isOpening && row.client !== '-' && (
                  <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 0.5 }}>{row.client}</Typography>
                )}
                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Box>
                    {row.debit > 0 && (
                      <>
                        <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Money Out</Typography>
                        <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: '#f44336' }}>{formatCurrency(row.debit)}</Typography>
                      </>
                    )}
                    {row.credit > 0 && (
                      <>
                        <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Money In</Typography>
                        <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: '#4caf50' }}>{formatCurrency(row.credit)}</Typography>
                      </>
                    )}
                  </Box>
                  <Box textAlign="right">
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Balance</Typography>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700 }}>{formatCurrency(row.balance)}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
          {/* Mobile Summary */}
          {!loading && data.length > 0 && (
            <Card sx={{ bgcolor: isDark ? 'rgba(76,175,80,0.08)' : '#e8f5e9' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, mb: 1 }}>Totals</Typography>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Box>
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Total Out</Typography>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, color: '#f44336' }}>{formatCurrency(totalDebit)}</Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Total In</Typography>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, color: '#4caf50' }}>{formatCurrency(totalCredit)}</Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Closing Balance</Typography>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700 }}>{formatCurrency(closingBalance)}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        </Stack>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Date</TableCell>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Particulars / Description</TableCell>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Client</TableCell>
                  <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Money Out</TableCell>
                  <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Money In</TableCell>
                  <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Running Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                ) : paginatedData.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><Typography color="text.secondary" sx={{ fontSize: fontSize.sm }}>No transactions found</Typography></TableCell></TableRow>
                ) : (
                  <>
                    {paginatedData.map((row) => (
                      <TableRow key={row.id} hover
                        sx={row.isOpening ? { bgcolor: isDark ? 'rgba(66,165,245,0.08)' : '#e3f2fd' } : {}}>
                        <TableCell sx={{ fontSize: '13px', py: 1, color: 'text.secondary' }}>{formatDate(row.date)}</TableCell>
                        <TableCell sx={{ fontSize: '13px', py: 1, fontWeight: row.isOpening ? 700 : 400 }}>
                          {row.description}
                          {row.reference && <Typography component="div" sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>{row.reference}</Typography>}
                        </TableCell>
                        <TableCell sx={{ fontSize: '13px', py: 1, color: row.client === '-' ? 'text.secondary' : 'text.primary' }}>{row.client}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '13px', py: 1, fontWeight: 500, color: row.debit > 0 ? '#f44336' : 'text.secondary' }}>
                          {row.debit > 0 ? formatCurrency(row.debit) : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '13px', py: 1, fontWeight: 500, color: row.credit > 0 ? '#4caf50' : 'text.secondary' }}>
                          {row.credit > 0 ? formatCurrency(row.credit) : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '13px', py: 1, fontWeight: 700 }}>{formatCurrency(row.balance)}</TableCell>
                      </TableRow>
                    ))}
                    {/* Total Row */}
                    <TableRow sx={{ bgcolor: isDark ? 'rgba(76,175,80,0.08)' : '#e8f5e9' }}>
                      <TableCell colSpan={3} sx={{ fontSize: '13px', fontWeight: 700, py: 1.5 }}>
                        TOTALS
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 700, py: 0.75, color: '#f44336' }}>
                        {formatCurrency(totalDebit)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 700, py: 0.75, color: '#4caf50' }}>
                        {formatCurrency(totalCredit)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 700, py: 0.75 }}>
                        {formatCurrency(closingBalance)}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={data.length} page={page}
            onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>
      )}
    </Box>
  );
};

export default WalletBankReport;
